import React from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { useMyAgentProfile } from '../../hooks/useAgent';
import { formatDate } from '../../utils/formatters';
import { Store, MapPin, Mail, Phone, Globe, Briefcase } from 'lucide-react';

const STATUS_VARIANT: Record<string, any> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', DISABLED: 'danger' };

export const AgentProfile: React.FC = () => {
  const { data, isLoading } = useMyAgentProfile();

  if (isLoading) return <AgentLayout><Spinner size="lg" /></AgentLayout>;

  const profile = data?.data;
  if (!profile) return <AgentLayout><p className="text-gray-400 dark:text-gray-500 text-center py-12">No agent profile found.</p></AgentLayout>;

  return (
    <AgentLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Agent Profile</h1>
          <Badge variant={STATUS_VARIANT[profile.status]}>{profile.status}</Badge>
          {profile.canPayoutLargeWins && <Badge variant="info">Super Agent</Badge>}
        </div>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Your agent registration details</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Store Information</h3>
          <div className="space-y-4">
            {[
              { icon: Store, label: 'Business Name', value: profile.businessName || '—' },
              { icon: Briefcase, label: 'Primary Activity', value: profile.storePrimaryActivity },
              { icon: MapPin, label: 'Store Address', value: profile.storeAddress },
              { icon: Mail, label: 'Store Contact Email', value: profile.storeContactEmail },
              { icon: Phone, label: 'Store Contact Phone', value: profile.storeContactPhone },
              { icon: Globe, label: 'Website', value: profile.website || '—' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Account Info</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <Badge variant={STATUS_VARIANT[profile.status]}>{profile.status}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Large Win Payout</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{profile.canPayoutLargeWins ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Applied</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(profile.createdAt)}</span>
            </div>
            {profile.approvedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Approved</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(profile.approvedAt)}</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </AgentLayout>
  );
};
